// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';

import { Dealer_Viewable_Driver_Info, Moves } from '@gql/base-types';

import { useCustomerConfig } from '@store';
import { isMoveLite } from '@utils/tiers';
import { getCleansedPhoneNumber } from '@services/formatService';
import { getFormattedMoveTypeFromMove, getFormattedVehicleFromMove } from '@services/moveService';
import { getMoveInputData, getWorkflowData } from '@services/utilityService';

import { css } from '@emotion/css';
import { Theme, useTheme, InputAdornment, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { BiBarcode, BiClipboard, BiDetail, BiPencil, BiPhone, BiSolidContact, BiUser } from 'react-icons/bi';

import { Divider } from '@hopdrive/sdk-ui';
import InfoItem from '@components/InfoItem';
import MoveIdBadge from '@components/Badges/MoveIdBadge';
import MoveStatusBadge from '@components/Badges/MoveStatusBadge';

import MoveDetailsVehicleImage from '@features/moves/components/MoveDetailsVehicleImage';
import MoveDetailsDriverImage from '@features/moves/components/MoveDetailsDriverImage';
import { getMoveHeaderTime } from '@features/moves/utils/moveHeaderTime';

const maxContactLength = 128;
const maxNotesLength = 512;

// TYPES ---------------------------------------------------------------- //

interface MoveDetailsInfoProps {
  move?: Moves;
  driver?: Dealer_Viewable_Driver_Info;
  isPrinting?: boolean;
  isEditing?: boolean;
  hasDriver?: boolean;
  compact?: boolean;
  editContact?: string;
  editNotes?: string;
  editReferenceNum?: string;
  editVin?: string;
  editStock?: string;
  setEditContact?: (value: string) => void;
  setEditNotes?: (value: string) => void;
  setEditReferenceNum?: (value: string) => void;
  setEditVin?: (value: string) => void;
  setEditStock?: (value: string) => void;
  editConsumerName?: string;
  editConsumerPhone?: string;
  setEditConsumerName?: (value: string) => void;
  setEditConsumerPhone?: (value: string) => void;
  onEditVehicle?: () => void;
  onEditOdometer?: () => void;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveDetailsInfo = ({
  move,
  driver,
  isPrinting,
  isEditing,
  hasDriver,
  compact,
  editContact,
  editNotes,
  editReferenceNum,
  editVin,
  editStock,
  setEditContact,
  setEditNotes,
  setEditReferenceNum,
  setEditVin,
  setEditStock,
  editConsumerName,
  editConsumerPhone,
  setEditConsumerName,
  setEditConsumerPhone,
  onEditVehicle,
  onEditOdometer,
}: MoveDetailsInfoProps) => {
  const theme = useTheme();
  const cls = useStyles(theme, { isPrinting, compact });

  const customerConfig = useCustomerConfig();
  // Per-order: the move's own fact, so a self-managed move reads correctly on any rooftop.
  const isLiteTier = isMoveLite(move);
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';
  const headerTime = getMoveHeaderTime(move);

  // Move input data
  const moveInputData = getMoveInputData(move?.config?.moveInput, `move_details`, true);

  const moveInputDataEven: any[] = [];
  const moveInputDataOdd: any[] = [];

  moveInputData?.length &&
    moveInputData?.forEach((wd, i) => {
      if (i % 2) moveInputDataEven.push(wd);
      else moveInputDataOdd.push(wd);
    });

  // Pickup workflow data
  const pickupWorkflowData = getWorkflowData(
    `pickup`,
    move?.pickup_workflow_data,
    `move_details`,
    true,
    move?.workflowset
  );

  const pickupWorkflowEven: any[] = [];
  const pickupWorkflowOdd: any[] = [];

  pickupWorkflowData?.length &&
    pickupWorkflowData
      ?.filter(wd => !wd.key.includes(`Damages`))
      ?.forEach((wd, i) => {
        if (i % 2) pickupWorkflowEven.push(wd);
        else pickupWorkflowOdd.push(wd);
      });

  // Delivery workflow data
  const deliveryWorkflowData = getWorkflowData(
    `delivery`,
    move?.delivery_workflow_data,
    `move_details`,
    true,
    move?.workflowset
  );

  const deliveryWorkflowEven: any[] = [];
  const deliveryWorkflowOdd: any[] = [];

  deliveryWorkflowData?.length &&
    deliveryWorkflowData
      ?.filter(wd => !wd.key.includes(`Damages`))
      ?.forEach((wd, i) => {
        if (i % 2) deliveryWorkflowEven.push(wd);
        else deliveryWorkflowOdd.push(wd);
      });

  /** Render a workflow data item, with an inline "Edit" link for odometer entries */
  const renderWorkflowItem = (wd: any) => {
    if (wd?.key === 'Odometer' && onEditOdometer) {
      return (
        <div className={cls.vehicleEditRow}>
          <Typography
            variant='body2'
            sx={{ whiteSpace: `nowrap`, fontSize: `14px`, color: theme?.palette?.text?.disabled }}
          >
            {wd.key}
          </Typography>
          <div className={cls.flexLine} />
          <Typography variant='body2' sx={{ whiteSpace: `nowrap`, fontSize: `14px` }}>
            {wd?.val != null ? String(wd.val) : `N/A`}
          </Typography>
          <Tooltip title='Correct driver odometer' placement='top'>
            <span onClick={onEditOdometer} className={cls.odometerEditIcon}>
              <BiPencil size={14} />
            </span>
          </Tooltip>
        </div>
      );
    }
    return <InfoItem keyText={wd?.key} valueText={wd?.val != null ? String(wd.val) : `N/A`} />;
  };

  if (!move) return null;
  return (
    <>
      <div className={cls.info}>
        {!compact && <MoveDetailsVehicleImage move={move} />}

        <div className={cls.infoPaper}>
          <Paper variant={isPrinting ? `customPrint` : `custom`} sx={{ padding: compact ? `12px` : `16px` }}>
            <div className={cls.titleFlex}>
              <div className={cls.titleFlexItem} style={{ textAlign: `left` }}>
                <Tooltip title='HopDrive Move ID' placement='top'>
                  <MoveIdBadge move={move} />
                </Tooltip>
              </div>

              <div className={cls.titleFlexItem} style={{ textAlign: `center` }}>
                <Tooltip title={headerTime.label} placement='top'>
                  <Typography variant='h5' sx={{ lineHeight: 1, fontWeight: 600 }}>
                    {headerTime.time ? dayjs(headerTime.time).format('M/D/YYYY h:mm A') : 'N/A'}
                  </Typography>
                </Tooltip>
              </div>

              <div className={cls.titleFlexItem} style={{ textAlign: `right` }}>
                <Tooltip title='Status' placement='top'>
                  <MoveStatusBadge move={move} />
                </Tooltip>
              </div>
            </div>

            <div className={cls.sectionFlex}>
              <div className={cls.sectionFlexItem}>
                <InfoItem keyText={`Move Type`} valueText={getFormattedMoveTypeFromMove(move) || `N/A`} />
                {isEditing && !hasDriver && onEditVehicle ? (
                  <div className={cls.vehicleEditRow}>
                    <Typography
                      variant='body2'
                      sx={{ whiteSpace: `nowrap`, fontSize: `14px`, color: theme?.palette?.text?.disabled }}
                    >
                      Vehicle
                    </Typography>
                    <div className={cls.flexLine} />

                    <Typography
                      variant='body2'
                      onClick={onEditVehicle}
                      sx={{
                        marginLeft: `12px`,
                        color: theme?.palette?.primary?.main,
                        cursor: `pointer`,
                        whiteSpace: `nowrap`,
                        '&:hover': { textDecoration: `underline` },
                      }}
                    >
                      Click to edit vehicle
                    </Typography>
                  </div>
                ) : (
                  <InfoItem keyText={`Vehicle`} valueText={getFormattedVehicleFromMove(move) || `N/A`} />
                )}
                <InfoItem keyText={`Transmission`} valueText={move?.manual_flag ? `Manual` : `Automatic`} />
                <InfoItem keyText={`Odometer`} valueText={move?.vehicle_odometer || `N/A`} />
                {isEditing ? (
                  <TextField
                    fullWidth
                    name='move-details-reference-num'
                    placeholder={`Enter ${refLabel}...`}
                    value={editReferenceNum || ''}
                    onChange={e => {
                      const value = e?.target?.value || '';
                      if (value?.length > 48) return;
                      if (setEditReferenceNum) setEditReferenceNum(value);
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position='start'>
                            <BiClipboard size={20} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                ) : (
                  <InfoItem keyText={refLabel} valueText={move?.reference_num || `N/A`} />
                )}
                {isEditing &&
                (!hasDriver ||
                  (isLiteTier &&
                    (!move?.status ||
                      move?.status === 'dispatched' ||
                      move?.status === 'pickup started' ||
                      move?.status === 'pickup arrived'))) ? (
                  <TextField
                    fullWidth
                    name='move-details-vin'
                    placeholder='Enter VIN...'
                    value={editVin || ''}
                    onChange={e => {
                      const value = e?.target?.value || '';
                      if (value?.length > 17) return;
                      if (setEditVin) setEditVin(value);
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position='start'>
                            <BiBarcode size={20} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                ) : (
                  <InfoItem keyText={`VIN`} valueText={move?.vehicle_vin || `N/A`} />
                )}
                {isEditing && !hasDriver ? (
                  <TextField
                    fullWidth
                    name='move-details-stock'
                    placeholder='Enter Stock #...'
                    value={editStock || ''}
                    onChange={e => {
                      const value = e?.target?.value || '';
                      if (value?.length > 24) return;
                      if (setEditStock) setEditStock(value);
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position='start'>
                            <BiClipboard size={20} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                ) : (
                  <InfoItem keyText={`Stock`} valueText={move?.vehicle_stock || `N/A`} />
                )}
              </div>

              <div className={cls.sectionFlexItem}>
                <InfoItem keyText={`Driver`} valueText={move?.driver_name || `N/A`} />
                <InfoItem keyText={`Market`} valueText={move?.customer?.name || `N/A`} />
                <InfoItem keyText={`Payer`} valueText={move?.payer?.name || move?.customer?.name || `N/A`} />
                <InfoItem keyText={`Workflow Name`} valueText={move?.workflowset?.name || `-`} />
                <InfoItem keyText={`Distance`} valueText={(move?.lane?.distance_miles?.toFixed(1) || `?`) + ` mi`} />

                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    name='move-details-contact'
                    placeholder='Enter contact info...'
                    value={editContact}
                    onChange={e => {
                      const value = e?.target?.value || ``;
                      if (value?.length > maxContactLength) return;
                      if (setEditContact) setEditContact(value);
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position='start'>
                            <BiSolidContact size={20} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                ) : (
                  <InfoItem keyText={`Contact Info`} valueText={move?.dealer_contact || `N/A`} />
                )}

                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    name='move-details-notes'
                    placeholder='Enter additional notes/instructions...'
                    value={editNotes}
                    onChange={e => {
                      const value = e?.target?.value || ``;
                      if (value?.length > maxNotesLength) return;
                      if (setEditNotes) setEditNotes(value);
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position='start'>
                            <BiDetail size={20} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                ) : (
                  <InfoItem keyText={`Notes/Instructions`} valueText={move?.move_details || `N/A`} />
                )}
              </div>
            </div>
          </Paper>

          {move?.consumer_name || move?.consumer_phone || (isEditing && !hasDriver) ? (
            <Paper variant={isPrinting ? `customPrint` : `custom`} sx={{ flex: 1, padding: `16px`, marginTop: `16px` }}>
              <div className={cls.sectionFlex}>
                <div className={cls.sectionFlexItem}>
                  {isEditing && !hasDriver ? (
                    <TextField
                      fullWidth
                      name='move-details-consumer-name'
                      placeholder='Enter customer name...'
                      value={editConsumerName || ''}
                      onChange={e => {
                        const value = e?.target?.value || '';
                        if (value?.length > 64) return;
                        if (setEditConsumerName) setEditConsumerName(value);
                      }}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position='start'>
                              <BiUser size={20} />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  ) : (
                    <InfoItem keyText={`Customer Name`} valueText={move?.consumer_name || `N/A`} />
                  )}
                </div>

                <div className={cls.sectionFlexItem}>
                  {isEditing && !hasDriver ? (
                    <TextField
                      fullWidth
                      name='move-details-consumer-phone'
                      placeholder='Enter customer phone...'
                      value={editConsumerPhone || ''}
                      onChange={e => {
                        const value = e?.target?.value || '';
                        if (value?.length > 20) return;
                        if (setEditConsumerPhone) setEditConsumerPhone(value);
                      }}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position='start'>
                              <BiPhone size={20} />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  ) : (
                    <InfoItem
                      keyText={`Customer Phone`}
                      valueText={getCleansedPhoneNumber(move?.consumer_phone) || `N/A`}
                    />
                  )}
                </div>
              </div>
            </Paper>
          ) : null}

          {move?.cancel_status ? (
            <Paper variant={isPrinting ? `customPrint` : `custom`} sx={{ flex: 1, padding: `16px`, marginTop: `16px` }}>
              <div className={cls.sectionFlex}>
                <div className={cls.sectionFlexItem}>
                  <InfoItem
                    keyText={`Cancel Reason`}
                    valueText={move?.cancel_reason || `Cancel reason not provided.`}
                  />
                </div>
              </div>
            </Paper>
          ) : null}

          {moveInputData?.length ? (
            <>
              <Divider spacer mb={6} tip='View additional data added by the creator of the move.'>
                Move Input Data
              </Divider>

              <Paper variant={isPrinting ? `customPrint` : `custom`} sx={{ flex: 1, padding: `16px` }}>
                <div className={cls.sectionFlex}>
                  <div className={cls.sectionFlexItem}>
                    {moveInputDataOdd.map((wd, i) => (
                      <React.Fragment key={`move-details-move-input-odd-${i}`}>
                        <InfoItem keyText={wd?.key} valueText={wd?.val != null ? String(wd.val) : `N/A`} />
                      </React.Fragment>
                    ))}
                  </div>

                  <div className={cls.sectionFlexItem}>
                    {moveInputDataEven.map((wd, i) => (
                      <React.Fragment key={`move-details-move-input-even-${i}`}>
                        <InfoItem keyText={wd?.key} valueText={wd?.val != null ? String(wd.val) : `N/A`} />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Paper>
            </>
          ) : null}

          {pickupWorkflowData?.length ? (
            <>
              <Divider spacer mb={6} tip='View additional data captured by the driver on the pickup'>
                Pickup Workflow Data
              </Divider>

              <Paper variant={isPrinting ? `customPrint` : `custom`} sx={{ flex: 1, padding: `16px` }}>
                <div className={cls.sectionFlex}>
                  <div className={cls.sectionFlexItem}>
                    {pickupWorkflowOdd.map((wd, i) => (
                      <React.Fragment key={`move-details-pickup-workflow-odd-${i}`}>
                        {renderWorkflowItem(wd)}
                      </React.Fragment>
                    ))}
                  </div>

                  <div className={cls.sectionFlexItem}>
                    {pickupWorkflowEven.map((wd, i) => (
                      <React.Fragment key={`move-details-pickup-workflow-even-${i}`}>
                        {renderWorkflowItem(wd)}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Paper>
            </>
          ) : null}

          {deliveryWorkflowData?.length ? (
            <>
              <Divider spacer mb={6} tip='View additional data captured by the driver on the delivery'>
                Delivery Workflow Data
              </Divider>

              <Paper variant={isPrinting ? `customPrint` : `custom`} sx={{ flex: 1, padding: `16px` }}>
                <div className={cls.sectionFlex}>
                  <div className={cls.sectionFlexItem}>
                    {deliveryWorkflowOdd.map((wd, i) => (
                      <React.Fragment key={`move-details-delivery-workflow-odd-${i}`}>
                        {renderWorkflowItem(wd)}
                      </React.Fragment>
                    ))}
                  </div>

                  <div className={cls.sectionFlexItem}>
                    {deliveryWorkflowEven.map((wd, i) => (
                      <React.Fragment key={`move-details-delivery-workflow-even-${i}`}>
                        {renderWorkflowItem(wd)}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Paper>
            </>
          ) : null}
        </div>

        {!compact && <MoveDetailsDriverImage driver={driver} />}
      </div>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme, props?: any) => ({
  info: css`
    display: flex;
    gap: ${props?.compact ? `0` : `18px`};
  `,
  infoPaper: css`
    display: flex;
    flex-direction: column;
    gap: ${props?.compact ? `12px` : `0`};
    flex: 1;
  `,

  titleFlex: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${props?.compact ? `8px` : `12px`};
    margin-bottom: ${props?.compact ? `12px` : `16px`};
  `,
  titleFlexItem: css`
    flex: 1;
    cursor: pointer;
  `,

  sectionFlex: css`
    display: flex;
    flex-direction: ${props?.compact ? `column` : `row`};
    gap: ${props?.compact ? `6px` : `20px`};
    ${theme?.breakpoints?.down('md')} {
      flex-direction: ${props?.isPrinting ? `row` : `column`};
      gap: ${props?.isPrinting ? `20px` : `6px`};
    }
  `,
  sectionFlexItem: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    word-break: break-word;
  `,

  flex: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 6px;
  `,
  flexLine: css`
    flex: 1;
    border-top: 1px dashed ${theme?.palette?.action?.focus};
    margin: 9px 0;
  `,

  vehicleEditRow: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 6px;
    word-break: break-word;
  `,

  odometerEditIcon: css`
    position: relative;
    display: inline-flex;
    align-items: center;
    margin-left: 2px;
    top: 2px;
    cursor: pointer;
    color: ${theme?.palette?.action?.active};
    border-radius: 4px;
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 40px;
      height: 40px;
      transform: translate(-50%, -50%);
      border-radius: 50%;
    }
    &:hover {
      color: ${theme?.palette?.text?.primary};
    }
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MoveDetailsInfo;
